import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useCustomerCategories } from "@/hooks/useCustomerCategories";
import { useCustomerGroups } from "@/hooks/useCustomerGroups";
import { useCustomerData } from "@/hooks/useCustomerData";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Upload, ArrowUpDown, Download, UsersRound } from "lucide-react";
import CustomerImportDialog from "./CustomerImportDialog";
import { exportToCsv } from "@/lib/csvExport";
import { format } from "date-fns";
import { CustomerTable } from "./customers/CustomerTable";
import { CategoryFilterBadges, CustomerFormComponent } from "./customers/CustomerCategoryManager";
import { emptyForm } from "./customers/types";
import type { Customer, CustomerForm } from "./customers/types";

const PAGE_SIZE = 20;

export default function AdminCustomers() {
  const { t } = useTranslation("admin");
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { categories } = useCustomerCategories(organization?.id);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [form, setForm] = useState<CustomerForm>(emptyForm);
  const [exporting, setExporting] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [importOpen, setImportOpen] = useState(false);
  const [sortBy, setSortBy] = useState("created_desc");
  const [customerTypeFilter, setCustomerTypeFilter] = useState("all");
  const [genderFilter, setGenderFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  // UPPDRAG-47: activity-window filter on pre-computed last_active_at.
  // 'all' (default) hits no filter; the rest map to >= now-N-days or
  // the inverse "inactive" predicate (older than 1 year OR NULL).
  const [activityFilter, setActivityFilter] = useState("all");
  const { groups } = useCustomerGroups(organization?.id);

  const { customers, totalCount, isLoading, addCustomer } = useCustomerData(
    organization?.id,
    { search, page, pageSize: PAGE_SIZE, sortBy, categoryFilter, customerTypeFilter, genderFilter, sourceFilter, groupFilter, activityFilter },
  );

  // --- Helpers ---

  // 010: listan levereras nu av get_customers_admin_list, som returnerar en
  // trimmad rad (bara fälten UI:t använder) plus aggregaten — inte hela
  // customers-raden. Bara id behövs här.
  const openDetail = (c: { id: string }) => {
    navigate(`/manager/customers/${c.id}`);
  };

  const handleAddSuccess = () => {
    setShowAddDialog(false);
    setForm(emptyForm);
  };

  // G-020: export must cover ALL of the org's customers, not the current
  // ~20-row page — loop the pagination server-side at click time.
  const handleExport = async () => {
    if (!organization?.id || exporting) return;
    setExporting(true);
    try {
      const BATCH = 1000;
      const all: Pick<Customer, "customer_number" | "full_name" | "email" | "phone" | "category_id" | "created_at">[] = [];
      for (let from = 0; ; from += BATCH) {
        const { data, error } = await supabase
          .from("customers")
          .select("customer_number, full_name, email, phone, category_id, created_at")
          .eq("organization_id", organization.id)
          .order("created_at", { ascending: false })
          .range(from, from + BATCH - 1);
        if (error) throw error;
        all.push(...((data ?? []) as typeof all));
        if (!data || data.length < BATCH) break;
      }
      const rows = all.map((c) => ({
        [t("customersPage.csvCustomerNumber")]: `K-${c.customer_number ?? ""}`,
        [t("customersPage.csvName")]: c.full_name ?? "",
        [t("customersPage.csvEmail")]: c.email ?? "",
        [t("customersPage.csvPhone")]: c.phone ?? "",
        [t("customersPage.csvCategory")]: categories.find((cat) => cat.id === c.category_id)?.name ?? t("customersPage.csvCategoryDefault"),
        [t("customersPage.csvCreated")]: format(new Date(c.created_at), "yyyy-MM-dd"),
      }));
      exportToCsv("kunder.csv", rows);
    } catch {
      toast.error(t("customersPage.exportFailed"));
    } finally {
      setExporting(false);
    }
  };

  // --- Render ---

  return (
    <div className="space-y-3">
      {/* Search + Add */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("customersPage.searchPlaceholder")}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="pl-9 h-9"
          />
        </div>
        <Button size="sm" variant="outline" onClick={() => setImportOpen(true)}>
          <Upload className="h-4 w-4 mr-1" /> {t("common.import")}
        </Button>
        {/* UPPDRAG-47: extended sort options. Pre-computed columns
            (last_active_at, last_booking_at, total_bookings, total_spent_minor)
            keep the query server-side and cheap. Wider trigger to fit the
            longer Swedish labels like "Längst sedan aktiv". */}
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(0); }}>
          <SelectTrigger className="h-9 w-[200px]"><ArrowUpDown className="h-3.5 w-3.5 mr-1" /><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="created_desc">{t("customersPage.sortNewest")}</SelectItem>
            <SelectItem value="created_asc">{t("customersPage.sortOldest")}</SelectItem>
            <SelectItem value="name_asc">{t("customersPage.sortNameAsc")}</SelectItem>
            <SelectItem value="name_desc">{t("customersPage.sortNameDesc")}</SelectItem>
            <SelectItem value="active_desc">{t("customersPage.sortActiveDesc")}</SelectItem>
            <SelectItem value="active_asc">{t("customersPage.sortActiveAsc")}</SelectItem>
            <SelectItem value="last_booking_desc">{t("customersPage.sortLastBookingDesc")}</SelectItem>
            <SelectItem value="bookings_desc">{t("customersPage.sortBookingsDesc")}</SelectItem>
            <SelectItem value="bookings_asc">{t("customersPage.sortBookingsAsc")}</SelectItem>
            <SelectItem value="spent_desc">{t("customersPage.sortSpentDesc")}</SelectItem>
            <SelectItem value="spent_asc">{t("customersPage.sortSpentAsc")}</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" onClick={handleExport} disabled={exporting}>
          <Download className="h-4 w-4 mr-1" /> {exporting ? t("customersPage.exporting") : t("common.export")}
        </Button>
        <Button size="sm" variant="outline" onClick={() => navigate("/manager/customer-groups")}>
          <UsersRound className="h-4 w-4 mr-1" /> {t("customerGroups.title")}
        </Button>
        <Button size="sm" onClick={() => { setForm(emptyForm); setShowAddDialog(true); }}>
          <Plus className="h-4 w-4 mr-1" /> {t("customersPage.addCustomer")}
        </Button>
      </div>

      {/* Membership filter badges */}
      <CategoryFilterBadges
        categories={categories}
        categoryFilter={categoryFilter}
        onFilterChange={(v) => { setCategoryFilter(v); setPage(0); }}
      />

      {/* Additional filters */}
      <div className="flex gap-2 flex-wrap">
        <Select value={customerTypeFilter} onValueChange={(v) => { setCustomerTypeFilter(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder={t("customerFields.filterCustomerType")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("customerFields.allTypes")}</SelectItem>
            <SelectItem value="individual">{t("customerFields.individual")}</SelectItem>
            <SelectItem value="organization">{t("customerFields.organization")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={genderFilter} onValueChange={(v) => { setGenderFilter(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[120px] text-xs"><SelectValue placeholder={t("customerFields.filterGender")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("customerFields.allGenders")}</SelectItem>
            <SelectItem value="male">{t("customerFields.male")}</SelectItem>
            <SelectItem value="female">{t("customerFields.female")}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[130px] text-xs"><SelectValue placeholder={t("customerFields.filterSource")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("customerFields.allSources")}</SelectItem>
            <SelectItem value="manual">{t("customerFields.sourceManual")}</SelectItem>
            <SelectItem value="matchi">{t("customerFields.sourceMatchi")}</SelectItem>
          </SelectContent>
        </Select>
        {/* UPPDRAG-47: activity-window filter. Maps to a single server-side
            predicate on customers.last_active_at. 'inactive' uses an OR to
            also catch rows that were never active (NULL). */}
        <Select value={activityFilter} onValueChange={(v) => { setActivityFilter(v); setPage(0); }}>
          <SelectTrigger className="h-8 w-[180px] text-xs"><SelectValue placeholder={t("customersPage.filterActivity")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("customersPage.activityAll")}</SelectItem>
            <SelectItem value="active_30">{t("customersPage.activityLast30")}</SelectItem>
            <SelectItem value="active_90">{t("customersPage.activityLast90")}</SelectItem>
            <SelectItem value="active_365">{t("customersPage.activityLast365")}</SelectItem>
            <SelectItem value="inactive">{t("customersPage.activityInactive")}</SelectItem>
          </SelectContent>
        </Select>
        {groups.length > 0 && (
          <Select value={groupFilter} onValueChange={(v) => { setGroupFilter(v); setPage(0); }}>
            <SelectTrigger className="h-8 w-[150px] text-xs"><SelectValue placeholder={t("customerGroups.filterGroup")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("customerGroups.allGroups")}</SelectItem>
              {groups.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: g.color ?? "#6366f1" }} />
                    {g.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Customer list */}
      <CustomerTable
        customers={customers}
        categories={categories}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        onPageChange={setPage}
        onSelectCustomer={openDetail}
      />

      {/* Add customer dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("customersPage.addCustomerTitle")}</DialogTitle>
          </DialogHeader>
          <CustomerFormComponent
            form={form}
            setForm={setForm}
            emailDisabled={false}
            onSubmit={() => {
              addCustomer.mutate(form, { onSuccess: handleAddSuccess });
            }}
            submitLabel={t("common.add")}
            loading={addCustomer.isPending}
            categories={categories}
          />
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <CustomerImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        organizationId={organization?.id ?? ""}
        categories={categories}
      />

    </div>
  );
}
